import { useCallback } from 'react'
import { useSelector } from 'react-redux'
import { AnalyticsApplyPayload, AnalyticsEvent, AnalyticsRejectPayload } from '@typesApp/Analytics'
import { AnalyticsUtil } from '@utils/analyticsUtil'
import { connectorActionModuleSelector } from '@redux/slices/connectorSlice'
import { useAppTranslation } from './useAppTranslation'

type UseAnalyticsReturn = {
  pushOpenEvent: () => void
  pushApplyEvent: (payload: AnalyticsApplyPayload) => void
  pushRejectEvent: (payload: AnalyticsRejectPayload) => void
}

/**
 * Push insurance analytics events to commerce app:
 *  1. Insurance panel open
 *  2. Insurance apply success
 *  3. Insurance rejected
 */
export const useAnalytics = (): UseAnalyticsReturn => {
  const { t } = useAppTranslation({ keyPrefix: 'Analytics' })
  const actionModule = useSelector(connectorActionModuleSelector)

  const pushOpenEvent = useCallback(() => {
    actionModule?.sendAnalyticsEvent(AnalyticsUtil.createOpenEventData()).catch(error => console.warn(error))
  }, [actionModule])

  const pushApplyEvent = useCallback(
    (payload: AnalyticsApplyPayload) => {
      actionModule?.sendAnalyticsEvent(AnalyticsUtil.createApplyEventData(payload)).catch(error => console.warn(error))
    },
    [actionModule]
  )

  const pushRejectEvent = useCallback(
    (payload: AnalyticsRejectPayload) => {
      const { error, ...eventData } = AnalyticsUtil.createRejectEventData(payload)

      // add error message if applicable
      const message = error ? t(`Error.${error}`) : ''
      const decoratedData: AnalyticsEvent = {
        ...eventData,
        Error_Message: message,
      }

      actionModule?.sendAnalyticsEvent(decoratedData).catch(error => console.warn(error))
    },
    [t, actionModule]
  )

  return {
    pushOpenEvent,
    pushApplyEvent,
    pushRejectEvent,
  }
}
